import {
  MIN_num_rows,
  MIN_num_tables,
  MIN_num_tables_per_row,
} from "@duet/shared/pvComponents/projectParameters";
import { groupBy, sumBy } from "lodash";
import { BlockLayer, TableAndRowCounts } from "./BlockLayerModel";
import { Block } from "./BlockModel";
import { calculateBlockFrameParams } from "./calculateBlockFrameParams";
import { calculateBlockSectionStats } from "./calculateBlockSectionStats";
import { FrameFeature, partitionedFramesSelector } from "./frameUtil";

export interface BlockArrayParams {
  num_rows: number;
  num_total_tables: number;
  num_table_per_row: number;
  scaled_tables?: number;
  scaling_factor?: number;
}

export function calculateBlockArrayParams(
  block: Block & { isVisible?: boolean },
  blockLayers: BlockLayer[],
  sectionStats: Record<string, TableAndRowCounts>,
  frames: FrameFeature[] | undefined
): BlockArrayParams {
  if (frames && frames.length > 0) {
    return calculateBlockFrameParams(block, frames);
  }

  const selectedBlockSections = blockLayers.filter(
    (l) =>
      (l.type === "section" ||
        l.type === "guide_line" ||
        l.type === "panels") &&
      l.block_id === block.id
  );
  const num_rows = sumBy(selectedBlockSections, (l) => {
    return sectionStats[l.id]?.rows ?? 0;
  });
  const num_total_tables = sumBy(selectedBlockSections, (l) => {
    return sectionStats[l.id]?.tables ?? 0;
  });

  const num_table_per_row =
    num_rows === 0 ? 0 : Math.round(num_total_tables / num_rows);

  // Panel layouts are weird and don't often fit well into the rep-array
  // paradigm: a single string may consist of panels from multiple rows and
  // the total table count and we may not hit the minimum table count.
  // For now, just fudge these inputs to get simulations running.
  if (selectedBlockSections.some((l) => l.type === "panels")) {
    const scaledTablePerRow = Math.max(
      num_table_per_row,
      MIN_num_tables_per_row
    );
    const scaledRows = Math.max(
      Math.round(num_total_tables / scaledTablePerRow),
      MIN_num_rows
    );
    const scaledTables = scaledTablePerRow * scaledRows;
    const scaling_factor = num_total_tables / scaledTables;
    return {
      num_rows: scaledRows,
      num_total_tables,
      num_table_per_row: scaledTablePerRow,
      scaled_tables: scaledTables,
      scaling_factor,
    };
  }

  if (
    num_total_tables >= MIN_num_tables &&
    num_table_per_row < MIN_num_tables_per_row
  ) {
    const rescaledNumRows = Math.round(
      num_total_tables / MIN_num_tables_per_row
    );

    return {
      num_rows: rescaledNumRows,
      num_total_tables,
      num_table_per_row: MIN_num_tables_per_row,
    };
  }

  return {
    num_rows,
    num_total_tables,
    num_table_per_row,
  };
}

export function calculateBlockInputParams(
  blocks: Block[],
  blockLayers: BlockLayer[]
) {
  const frameLayers = blockLayers.filter((bl) => bl.type === "frame");

  const perimiterLayers = blockLayers.filter((bl) => bl.type === "perimeter");

  const blockLayersByBlockId = groupBy(
    blockLayers as unknown as BlockLayer[],
    "block_id"
  );

  const framesByBlockId = partitionedFramesSelector(
    perimiterLayers,
    frameLayers
  );

  const sectionStats = calculateBlockSectionStats(blocks, blockLayers);

  return blocks.reduce(
    (acc, _block) => {
      const block = _block as unknown as Block;
      if (block.pv_panel === null || block.pv_table === null) return acc;

      const blockLayers = blockLayersByBlockId[block.id] ?? [];
      if (blockLayers.length === 0) return acc;

      const frames = framesByBlockId[block.id];

      const { num_rows, num_total_tables, scaled_tables, scaling_factor } =
        calculateBlockArrayParams(
          block,
          blockLayers as unknown as BlockLayer[],
          sectionStats,
          frames
        );

      acc.push({
        id: block.id,
        num_rows,
        num_total_tables: scaled_tables ?? num_total_tables,
        scaling_factor,
      });

      return acc;
    },
    [] as Array<{
      id: string;
      num_rows: number;
      num_total_tables: number;
      scaling_factor: number | undefined;
    }>
  );
}
